import { useEffect, useRef, useState } from "react";
import { Download, Description, AttachFile, KeyboardArrowLeft, KeyboardArrowRight, ExpandMore as ExpandMoreIcon, UploadFile as UploadFileIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "sonner";
import {
  useDownloadEventDataFileMutation,
  useUploadWorkDocumentMutation,
} from "../../../store/services/timelineApiRTK";
import {
  ProcessedEventData,
  isFileAttachmentEventData,
} from "../../../types/EventData.types";
import { FILE_EVENT_TYPES } from "./fileEventTypes";

const ITEMS_PER_PAGE = 5;

// Samma regler som Kraftvyns DropzoneDialog
const MAX_FILES_PER_BATCH = 15;
const MAX_FILE_SIZE = 50_000_000; // 50 MB
const MAX_FILENAME_LENGTH = 50;

/** Kapar filnamnet till MAX_FILENAME_LENGTH med filändelsen bevarad. */
const truncateFilename = (name: string): string => {
  if (name.length <= MAX_FILENAME_LENGTH) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot) : "";
  if (ext.length >= MAX_FILENAME_LENGTH) return name.slice(0, MAX_FILENAME_LENGTH);
  const base = dot > 0 ? name.slice(0, dot) : name;
  return base.slice(0, MAX_FILENAME_LENGTH - ext.length) + ext;
};

interface FilesCardProps {
  events: ProcessedEventData[];
  workId: number;
}

const FileItem = ({ event }: { event: ProcessedEventData }) => {
  const [downloadFile, { isLoading }] = useDownloadEventDataFileMutation();

  const filename =
    event.parsedData && isFileAttachmentEventData(event.parsedData)
      ? event.parsedData.OrginalFilename
      : null;

  const handleDownload = async () => {
    if (!filename) return;
    try {
      await downloadFile({ eventDataId: event.Id, filename }).unwrap();
    } catch {
      toast.error("Det gick inte att ladda ner filen.");
    }
  };

  return (
    <ListItem sx={{ px: 0, py: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Description fontSize="small" color="action" />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body2" noWrap>
            {filename || event.displayName}
          </Typography>
        }
        secondary={
          <Typography variant="caption" color="text.secondary">
            {format(new Date(event.Time), "d MMM yyyy", { locale: sv })} —{" "}
            {event.CreatedBy.Name}
          </Typography>
        }
      />
      {filename && (
        <Button
          size="small"
          onClick={handleDownload}
          disabled={isLoading}
          sx={{ minWidth: "auto", ml: 1 }}
        >
          {isLoading ? <CircularProgress size={16} /> : <Download fontSize="small" />}
        </Button>
      )}
    </ListItem>
  );
};

const FilesCard = ({ events, workId }: FilesCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, { isLoading: isUploading }] =
    useUploadWorkDocumentMutation();

  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;

    if (files.length > MAX_FILES_PER_BATCH) {
      toast.error(`Max ${MAX_FILES_PER_BATCH} filer åt gången.`);
      return;
    }

    const validFiles = files
      .filter((file) => {
        if (file.size > MAX_FILE_SIZE) {
          toast.warning(`"${file.name}" hoppades över — max 50 MB per fil.`);
          return false;
        }
        return true;
      })
      .map((file) => {
        if (file.name.length <= MAX_FILENAME_LENGTH) return file;
        const newName = truncateFilename(file.name);
        toast.info(`Filnamnet kapades till "${newName}" (max ${MAX_FILENAME_LENGTH} tecken).`);
        return new File([file], newName, {
          type: file.type,
          lastModified: file.lastModified,
        });
      });

    let uploadedCount = 0;
    for (const file of validFiles) {
      try {
        await uploadFile({ workId, file }).unwrap();
        uploadedCount++;
      } catch {
        toast.error(`Det gick inte att ladda upp "${file.name}".`);
      }
    }

    if (uploadedCount > 0) {
      toast.success(
        uploadedCount === 1
          ? "Uppladdningen klar!"
          : `${uploadedCount} filer uppladdade!`
      );
      setExpanded(true);
      setPage(1);
    }
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    handleFiles(files);
  };

  // Ctrl+V med en bild/fil i urklipp laddar upp den. Ref-mönstret gör att
  // lyssnaren bara registreras en gång men alltid ser senaste handleFiles.
  const handleFilesRef = useRef(handleFiles);
  handleFilesRef.current = handleFiles;

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length === 0) return; // Vanlig textinklistring lämnas orörd
      e.preventDefault();

      // Urklippsbilder heter bara "image.png" — ge dem ett spårbart namn
      const named = files.map((file) => {
        if (!file.type.startsWith("image/")) return file;
        const ext = file.type.split("/")[1]?.replace("jpeg", "jpg") || "png";
        const name = `urklipp-${format(new Date(), "yyyy-MM-dd-HHmm")}.${ext}`;
        return new File([file], name, { type: file.type });
      });
      handleFilesRef.current(named);
    };

    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  // Räknare eftersom dragleave även triggas när muspekaren passerar barnelement
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragActive(false);
    if (isUploading) return;
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const fileEvents = events
    .filter((e) => FILE_EVENT_TYPES.includes(e.Type))
    .sort((a, b) => new Date(b.Time).getTime() - new Date(a.Time).getTime());

  const totalPages = Math.ceil(fileEvents.length / ITEMS_PER_PAGE);
  const paginatedFiles = fileEvents.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  return (
    <Card
      elevation={2}
      sx={{ position: "relative" }}
      onDragEnter={handleDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Utanför header-boxen så att det programmatiska klicket på inputen
          inte bubblar upp till accordion-togglens onClick */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        onChange={handleFilesSelected}
      />

      {dragActive && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            border: "2px dashed",
            borderColor: "primary.main",
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.9)",
            pointerEvents: "none",
          }}
        >
          <UploadFileIcon color="primary" />
          <Typography variant="body2" color="primary" fontWeight={600}>
            Släpp filer här för att ladda upp
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          cursor: "pointer",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Typography
          variant="subtitle2"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <AttachFile fontSize="small" />
          Filer ({fileEvents.length})
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button
            size="small"
            startIcon={
              isUploading ? (
                <CircularProgress size={14} />
              ) : (
                <UploadFileIcon fontSize="small" />
              )
            }
            disabled={isUploading}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Ladda upp fil
          </Button>
          {expanded && totalPages > 1 && (
            <>
              <Typography variant="caption" color="text.secondary">
                {page} / {totalPages}
              </Typography>
              <IconButton
                size="small"
                disabled={page === 1}
                onClick={(e) => {
                  e.stopPropagation();
                  setPage((p) => p - 1);
                }}
              >
                <KeyboardArrowLeft fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={page >= totalPages}
                onClick={(e) => {
                  e.stopPropagation();
                  setPage((p) => p + 1);
                }}
              >
                <KeyboardArrowRight fontSize="small" />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0, "&:last-child": { pb: 2 } }}>
          {paginatedFiles.length > 0 ? (
            <List disablePadding>
              {paginatedFiles.map((event) => (
                <FileItem key={event.Id} event={event} />
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
              <Typography variant="body2">Inga filer</Typography>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default FilesCard;
